import * as React from 'react';
import { resolve } from 'inversify-react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { Row, Col, Button } from 'antd';

import { ControllerProps } from 'src/router';
import { ToastNotifications } from 'src/core/ui/toast';
import { ObjectUtils } from 'src/core/utils/object';
import { PaymentListView } from '../../shared/payments/list/payment-list.view';
import { PaymentListDataService } from '../../shared/payments/list/payment-list.data';
import { CardController } from 'src/app/shared/card.controller';
import { SupplierEditorController } from 'src/app/products/suppliers/parts/supplier-editor.controller';
import { TextView } from 'src/core/ui/text.view';
import { DateView } from 'src/core/ui/date.view';
import { DropdownView } from 'src/core/ui/dropdown.view';

interface Props extends ControllerProps<{}> {
}

interface State {
	filters?: SupplierPaymentFilters;
}

@observer
export class SupplierPaymentList extends React.Component<Props, State> {
	@resolve
	private toast: ToastNotifications;

	@observable
	private dataService: PaymentListDataService;

	private filtersMap = {
		paymentCurrencyId: (value: number): string => `it.currencyId == ${value}`,
		paymentDateFrom: (value: Date): string => `it.paymentDate >= ${value.getTime()}`,
		paymentDateTo: (value: Date): string => `it.paymentDate <= ${value.getTime()}`,
		paymentMethod: (value: number): string => `it.method == ${value}`,
		paymentReference: (value: string): string => value === '' ? 'true' : `it.paymentReference.contains('${value}')`,
	};

	constructor(props: Readonly<Props>) {
		super(props);

		this.state = {
			filters: {}
		};
	}

	componentDidMount(): void {
		this.dataService = PaymentListDataService.make();

		this.dataService.addFilter('isDeleted', 'eq', false);
		this.dataService.addSorting('id', 'asc', true);
		this.dataService.addFilter('accountingTarget', 'eq', 4);
	}

	private handleApplyFilters = async (): Promise<void> => {
		const { setBusy } = this.props;
		const { filters } = this.state;

		setBusy(true);
		try {
			const predicate = Object.getOwnPropertyNames(filters)
				.filter((fieldName) => filters[fieldName] != null && fieldName in this.filtersMap)
				.map((fieldName) => this.filtersMap[fieldName](filters[fieldName]))
				.join(' && ');

			this.dataService.params = {};
			const params = Object.getOwnPropertyNames(filters)
				.filter((fieldName) => filters[fieldName] != null && !(fieldName in this.filtersMap))
				.forEach((fieldName) => {
					this.dataService.params[fieldName] = filters[fieldName];
				});

			await this.dataService.load((query) => predicate
				? query.filter(predicate)
				: query);
		} catch (error) {
			this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
		} finally {
			setBusy(false);
		}
	};

	private handleChangeFilter = (fieldName: keyof SupplierPaymentFilters, value: any = null): void => {
		const { filters } = this.state;
		const newFilters = { ...filters, [fieldName]: value };

		this.setState({ filters: newFilters });
	};

	render(): React.ReactNode {
		if (this.dataService == null) {
			return null;
		}

		const { filters } = this.state;

		return (
			<div className="window window-normal">
				<div className="panel">
					<h2>Supplier Payments</h2>
					<Row gutter={16}>
						<Col xs={6}>
							<SupplierEditorController
								multi
								mode="edit"
								label="Supplier"
								value={filters.suppliersId}
								onChange={(suppliers: SupplierSummary[]) => {
									this.handleChangeFilter('suppliersId', suppliers != null && suppliers.length > 0 ? suppliers.map(m => m.id) : null);
								}}
								setBusy={this.props.setBusy}
							/>
						</Col>
						<Col xs={6}>
							<TextView
								mode="edit"
								label="Invoice Reference Number"
								value={filters.invoiceReferenceNumber}
								onChanged={(value) => this.handleChangeFilter('invoiceReferenceNumber', value == null || value.trim() === '' ? null : value)}
							/>
						</Col>
						<Col xs={6}>
							<DropdownView
								label="Payment Method"
								model={filters.paymentMethod}
								onChanged={(m) => this.handleChangeFilter('paymentMethod', m != null ? m.value : null)}
								options={[
									{ value: 0, label: 'Cash' },
									{ value: 1, label: 'BankTransfer' },
									{ value: 2, label: 'Card' }
								]}
							/>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col xs={6}>
							<TextView
								mode="edit"
								label="Payment Reference"
								value={filters.paymentReference}
								onChanged={(value) => this.handleChangeFilter('paymentReference', value == null || value.trim() === '' ? null : value)}
							/>
						</Col>
						<Col xs={6}>
							<DateView
								mode="edit"
								label="Payment Date From"
								value={filters.paymentDateFrom}
								onChanged={(date) => this.handleChangeFilter('paymentDateFrom', date)}
								format="small-date"
								dateUsage="utc"
							/>
						</Col>
						<Col xs={6}>
							<DateView
								mode="edit"
								label="Payment Date To"
								value={filters.paymentDateTo}
								onChanged={(date) => this.handleChangeFilter('paymentDateTo', date)}
								format="small-date"
								dateUsage="utc"
							/>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col xs={8}>
							<Button
								icon="search"
								style={{ margin: '8px auto' }}
								onClick={this.handleApplyFilters}>
								Search
							</Button>
						</Col>
					</Row>
					<hr />
					<Row gutter={16}>
						<Col xs={24}>
							<CardController
								title="Payments"
								icon="barcode"
								className="card-borderless"
								mode="display">
								<PaymentListView
									hideStatus
									model={this.dataService}
									setBusy={this.props.setBusy}
								/>
							</CardController>
						</Col>
					</Row>
				</div>
			</div>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/accounting/suppliers/payment/supplier-payment-list.tsx